import React, { useState, useMemo, useRef } from 'react';
import { 
  Megaphone, Send, Users, Filter, Sparkles, CheckCircle, Clock, 
  AlertCircle, Phone, ArrowRight, Eye, Copy, Check, 
  Target, Zap, Play, CheckCircle2, MessageSquare, ShieldCheck,
  Upload, FileText, X, Search, Trash2, Smartphone, Download,
  UserPlus, Shield, Timer, BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useCustomers } from '../hooks/useCustomers';
import { buildAudience, inSegment } from '../utils/customers';
import { useCampaigns } from '../hooks/useCampaigns';
import { CAMPAIGN_DAILY_LIMIT, CAMPAIGN_GAP_SECONDS, CAMPAIGN_MAX_RECIPIENTS, buildRecipients, defaultCampaignName, estimateText } from '../utils/campaigns';
import CampaignHistory from './CampaignHistory';

const CAMPAIGN_VARIABLES = [
  { tag: '{{nombre}}', alt: '{cliente}', label: 'Nombre Cliente', desc: 'Ej: Carlos' },
  { tag: '{{negocio}}', alt: '{negocio}', label: 'Nombre Negocio', desc: 'Ej: SeryiFlow' },
  { tag: '{{servicio}}', alt: '{servicio}', label: 'Servicio / Promo', desc: 'Ej: Tratamiento VIP' },
  { tag: '{{descuento}}', alt: '{descuento}', label: 'Descuento', desc: 'Ej: 20% OFF' },
  { tag: '{{link_reserva}}', alt: '{link}', label: 'Enlace Reserva', desc: 'URL de reserva' },
  { tag: '{{telefono}}', alt: '{telefono}', label: 'Teléfono', desc: 'Número celular' }
];

export default function WhatsAppCampaigns({ activeBusiness, onUpdateBusiness, isReadOnlyDemo }) {
  const [selectedSegment, setSelectedSegment] = useState('all'); // 'all', 'lead', 'cliente', 'vip', 'inactive', 'recent'
  const [activeTemplateId, setActiveTemplateId] = useState('promo');
  const [customText, setCustomText] = useState('');
  const [sentClients, setSentClients] = useState({}); // { clientId: true }
  const [searchFilter, setSearchFilter] = useState('');
  
  // La base de clientes vive en Supabase (public.customers), la misma que ve la pantalla Clientes.
  const { customers, statsByKey, tableMissing, addCustomer, importCustomers } = useCustomers();

  const [showImportModal, setShowImportModal] = useState(false);
  const [rawImportText, setRawImportText] = useState('');

  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');

  // Rate Limiting / Anti-Bloqueo WhatsApp
  const [showRateLimitPanel, setShowRateLimitPanel] = useState(false);
  const { campaigns, messages: campaignMessages, missing: campaignsMissing, busy: isSendingWebhook, create: createCampaign, setStatus: setCampaignStatus } = useCampaigns();

  const textareaRef = useRef(null);

  // Plantillas prediseñadas de Alta Conversión
  const defaultTemplates = useMemo(() => [
    {
      id: 'promo',
      label: '🎁 Descuento de Reactivación',
      category: 'Ofertas',
      text: `Hola {{nombre}} ✨ Te extrañamos en *{{negocio}}*. Tenemos un descuento especial del *20% OFF* en tu próxima reserva esta semana. ¿Te agendamos un espacio? {{link_reserva}}`
    },
    {
      id: 'maintenance',
      label: '📅 Recordatorio de Mantenimiento',
      category: 'Recurrencia',
      text: `Hola {{nombre}} 👋 Ya es momento ideal para el mantenimiento de tu servicio en *{{negocio}}*. Tenemos cupos disponibles esta semana. ¡Reserva aquí tu hora preferida! 👉 {{link_reserva}}`
    },
    {
      id: 'review',
      label: '⭐ Pedido de Reseña VIP',
      category: 'Reputación',
      text: `Hola {{nombre}}, muchas gracias por confiar en *{{negocio}}*. ¿Cómo estuvo tu experiencia reciente? Nos ayudaría mucho si nos dejas tu opinión o recomendación. ⭐`
    },
    {
      id: 'launch',
      label: '🚀 Anuncio Nuevo Servicio/Menú',
      category: 'Lanzamiento',
      text: `¡Hola {{nombre}}! En *{{negocio}}* acabamos de lanzar un nuevo servicio exclusivo para ti. Escríbenos por aquí para enviarte los detalles y promociones de lanzamiento. 🥂`
    }
  ], []);

  // Inicializar o sincronizar el texto del editor cuando cambia la plantilla
  React.useEffect(() => {
    const found = defaultTemplates.find(t => t.id === activeTemplateId);
    if (found) {
      setCustomText(found.text);
    }
  }, [activeTemplateId, defaultTemplates]);

  // Insertar variable en la posición del cursor
  const handleInsertVariable = (varTag) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setCustomText(prev => prev + ' ' + varTag);
      return;
    }

    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const current = customText || '';
    const updated = current.substring(0, start) + varTag + current.substring(end);
    setCustomText(updated);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + varTag.length, start + varTag.length);
    }, 50);
  };

  // Respaldo para cuentas demo o si aún no existe la tabla customers: se arma con citas y pedidos, como antes.
  const legacyAudience = useMemo(() => {
    const clientsMap = {};

    const addAppointment = (appt) => {
      if (!appt.phone && !appt.telefono) return;
      const rawPhone = String(appt.phone || appt.telefono || '').replace(/\D/g, '');
      if (rawPhone.length < 7) return;

      const name = (appt.client || appt.cliente || appt.client_name || 'Cliente').trim();
      const dateStr = appt.date || appt.fecha || '';
      const amount = Number(appt.precio_cobrado || appt.precio || 0);

      if (!clientsMap[rawPhone]) {
        clientsMap[rawPhone] = {
          id: rawPhone,
          name,
          phone: rawPhone,
          totalSpent: 0,
          appointmentsCount: 0,
          lastVisit: dateStr || '2026-01-01',
          source: 'Citas'
        };
      }
      clientsMap[rawPhone].totalSpent += amount;
      clientsMap[rawPhone].appointmentsCount += 1;
      if (dateStr && dateStr > clientsMap[rawPhone].lastVisit) {
        clientsMap[rawPhone].lastVisit = dateStr;
      }
    };

    // 1. Citas desde activeBusiness (Supabase JOIN)
    const appointments = Array.isArray(activeBusiness?.appointments) ? activeBusiness.appointments : [];
    appointments.forEach(addAppointment);

    // 1b. Citas desde el cache local de AppointmentsView (fallback clave)
    //     Este cache es la fuente real cuando RLS bloquea el JOIN de Supabase
    if (activeBusiness?.id) {
      try {
        const cachedAppts = localStorage.getItem(`seryiflow_appointments_${activeBusiness.id}`);
        if (cachedAppts) {
          const parsed = JSON.parse(cachedAppts);
          if (Array.isArray(parsed)) {
            parsed.forEach(addAppointment);
          }
        }
      } catch (e) { /* ignore */ }
    }

    // 2. Pedidos de Restaurante / WhatsApp (Orders)
    const orders = Array.isArray(activeBusiness?.orders) ? activeBusiness.orders : [];
    orders.forEach(order => {
      const rawPhone = String(order.client_phone || order.phone || '').replace(/\D/g, '');
      if (rawPhone.length < 7) return;

      const name = (order.client_name || order.customer || 'Cliente').trim();
      const amount = Number(order.total || 0);
      const dateStr = order.created_at ? order.created_at.split('T')[0] : '2026-01-01';

      if (!clientsMap[rawPhone]) {
        clientsMap[rawPhone] = {
          id: rawPhone,
          name,
          phone: rawPhone,
          totalSpent: 0,
          appointmentsCount: 0,
          lastVisit: dateStr,
          source: 'Pedidos'
        };
      }
      clientsMap[rawPhone].totalSpent += amount;
      clientsMap[rawPhone].appointmentsCount += 1;
      if (dateStr > clientsMap[rawPhone].lastVisit) {
        clientsMap[rawPhone].lastVisit = dateStr;
      }
    });

    return Object.values(clientsMap).map(c => ({
      ...c,
      lastActivity: c.lastVisit,
      stage: c.appointmentsCount > 0 ? 'cliente' : 'lead'
    }));
  }, [activeBusiness]);

  const { audience, withoutPhone, notInterested } = useMemo(() => buildAudience(customers, statsByKey), [customers, statsByKey]);
  const fallbackToActivity = Boolean(isReadOnlyDemo) || tableMissing;
  const clientsDatabase = fallbackToActivity ? legacyAudience : audience;
  const contactsWithoutPhone = fallbackToActivity ? 0 : withoutPhone;
  const contactsNotInterested = fallbackToActivity ? 0 : notInterested;

  // Segmentación de audiencia en tiempo real
  const filteredClients = useMemo(() => {
    const now = new Date();
    const query = searchFilter.toLowerCase().trim();

    return clientsDatabase.filter(client => {
      // Filtro de búsqueda por nombre o teléfono
      if (query) {
        const matchesName = (client.name || '').toLowerCase().includes(query);
        const matchesPhone = String(client.phone || '').includes(query);
        if (!matchesName && !matchesPhone) return false;
      }

      return inSegment(client, selectedSegment, now);
    });
  }, [clientsDatabase, selectedSegment, searchFilter]);

  // Renderizar mensaje reemplazando variables dinámicas
  const renderMessageForClient = (client) => {
    const bizName = activeBusiness?.nombre_negocio || 'SeryiFlow CRM';
    const clientName = client?.name || 'Cliente';
    const bizSlug = activeBusiness?.slug || 'demo';
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://flow.seryiads.com';
    const bookingLink = `${origin}/app/${bizSlug}`;

    let msg = customText || '';
    
    // Reemplazo de {{nombre}} y {cliente}
    msg = msg.split('{{nombre}}').join(clientName)
             .split('{cliente}').join(clientName)
             .split('{{negocio}}').join(bizName)
             .split('{negocio}').join(bizName)
             .split('{{servicio}}').join('Tratamiento Exclusivo')
             .split('{servicio}').join('Tratamiento Exclusivo')
             .split('{{descuento}}').join('20% OFF')
             .split('{descuento}').join('20% OFF')
             .split('{{link_reserva}}').join(bookingLink)
             .split('{link}').join(bookingLink)
             .split('{{telefono}}').join(client?.phone || '')
             .split('{telefono}').join(client?.phone || '');

    return msg;
  };

  // Procesar importación masiva de contactos
  const handleProcessImport = async () => {
    if (!rawImportText.trim()) {
      toast.error("Pega al menos una línea con Nombre y Teléfono.");
      return;
    }

    const lines = rawImportText.split(/\r?\n/);
    const parsed = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) return;

      // Soportar formatos: "Nombre, Telefono" o "Nombre; Telefono" o "Telefono"
      const parts = trimmed.split(/[,;\t]/);
      let name = 'Cliente';
      let phone = '';

      if (parts.length >= 2) {
        name = parts[0].trim();
        phone = parts[1].replace(/\D/g, '');
      } else {
        phone = parts[0].replace(/\D/g, '');
      }

      if (phone.length >= 7) {
        parsed.push({ name: name || 'Cliente', phone });
      }
    });

    if (parsed.length === 0) {
      toast.error("No se encontraron números válidos (mínimo 7 dígitos).");
      return;
    }

    // Se guardan en la base de clientes (Supabase); los que ya existían se omiten.
    const added = await importCustomers(parsed);
    if (added === 0) return;
    setSelectedSegment('all');
    setShowImportModal(false);
    setRawImportText('');
    toast.success(`¡${added} contactos agregados a tu base de clientes! 🎉`);
  };

  // Agregar un contacto individual manualmente
  const handleAddSingleContact = async () => {
    const phone = newContactPhone.replace(/\D/g, '');
    if (!newContactName.trim()) {
      toast.error("Ingresa el nombre del contacto.");
      return;
    }
    if (phone.length < 7) {
      toast.error("Ingresa un número de teléfono válido (mínimo 7 dígitos).");
      return;
    }
    const ok = await addCustomer({ name: newContactName, phone });
    if (!ok) return;
    setNewContactName('');
    setNewContactPhone('');
    setShowAddContactModal(false);
  };

  // Botón para envío individual
  const handleSendSingle = (client) => {
    const msg = renderMessageForClient(client);
    const url = `https://api.whatsapp.com/send?phone=${client.phone}&text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');

    setSentClients(prev => ({ ...prev, [client.id]: true }));
    toast.success(`Abriendo chat de WhatsApp para ${client.name}...`);
  };

  // Lanzar campaña: se guarda en el servidor y n8n la envía de a un mensaje por minuto (no depende de esta pestaña).
  const handleBroadcastWebhook = async () => {
    if (filteredClients.length === 0) {
      toast.error("No hay clientes en este segmento para enviar la campaña.");
      return;
    }
    if (!customText.trim()) {
      toast.error("Escribe el mensaje de la campaña.");
      return;
    }
    const recipients = buildRecipients(filteredClients, renderMessageForClient);
    const extra = filteredClients.length > CAMPAIGN_MAX_RECIPIENTS ? `\n\nUna campaña admite máximo ${CAMPAIGN_MAX_RECIPIENTS} destinatarios: se enviará a los primeros ${CAMPAIGN_MAX_RECIPIENTS}.` : '';
    const ok = window.confirm(
      `Vas a lanzar la campaña a ${recipients.length} contactos.\n\n${estimateText(recipients.length)}\n` +
      `Para proteger tu número, salen máximo ${CAMPAIGN_DAILY_LIMIT} por día y no se escribe a quien pidió no recibir mensajes.${extra}\n\n¿Lanzar?`
    );
    if (!ok) return;
    const label = defaultTemplates.find((t) => t.id === activeTemplateId)?.label;
    const result = await createCampaign({ name: defaultCampaignName(label), templateId: activeTemplateId, recipients });
    if (!result) return;
    if (!result.id) {
      toast.error('Ninguno de los contactos se puede contactar (silenciados, sin número válido o fuera del modo prueba).');
      return;
    }
    const omitidos = result.omitidos > 0 ? ` (${result.omitidos} omitidos: sin número válido, repetidos o que no quieren mensajes)` : '';
    toast.success(`Campaña lanzada: ${result.creados} mensajes en cola${omitidos}. Puedes cerrar esta pestaña.`);
  };

  return (
    <div className="space-y-8 animate-fadeIn pb-12">
      {/* Cabecera Principal del Módulo */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-seryi-bg border border-seryi-bg-light p-6 md:p-8 rounded-3xl shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-seryi-green/5 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-seryi-green text-xs font-bold uppercase tracking-widest mb-2">
            <Megaphone size={15} className="animate-bounce" />
            <span>Marketing Conversacional & Retención</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-seryi-heading tracking-tight">
            Campañas y Difusión por WhatsApp
          </h2>
          <p className="text-sm text-seryi-text mt-1 max-w-xl">
            Segmenta tu base de clientes, personaliza el mensaje con variables en vivo y lanza promociones masivas para llenar tu agenda.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 relative z-10">
          <button
            onClick={() => setShowAddContactModal(true)}
            className="flex items-center gap-2 px-4 py-3 bg-seryi-bg-dark hover:bg-seryi-bg-light text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold transition-all shadow-md active:scale-95"
          >
            <UserPlus size={14} className="text-seryi-green" />
            <span>Agregar Contacto</span>
          </button>

          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-3 bg-seryi-bg-dark hover:bg-seryi-bg-light text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold transition-all shadow-md active:scale-95"
          >
            <Upload size={14} className="text-cyan-400" />
            <span>Importar CSV</span>
          </button>

          <button
            onClick={() => setShowRateLimitPanel(!showRateLimitPanel)}
            className="flex items-center gap-2 px-4 py-3 bg-seryi-bg-dark hover:bg-seryi-bg-light text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold transition-all shadow-md active:scale-95"
            title="Configuración Anti-Bloqueo WhatsApp"
          >
            <Shield size={14} className="text-amber-400" />
            <span className="hidden sm:inline">Anti-Ban</span>
          </button>

          <button
            onClick={handleBroadcastWebhook}
            disabled={filteredClients.length === 0 || isSendingWebhook}
            className={`flex items-center gap-2 px-6 py-3.5 rounded-2xl text-xs font-extrabold uppercase tracking-wider transition-all shadow-lg ${
              filteredClients.length === 0 || isSendingWebhook
                ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                : 'bg-seryi-green text-seryi-bg-dark hover:brightness-110 shadow-seryi-green/25 hover:scale-105 active:scale-95'
            }`}
          >
            <Zap size={16} />
            <span>{isSendingWebhook ? 'Lanzando…' : `🚀 Lanzar Campaña (${filteredClients.length})`}</span>
          </button>
        </div>
      </div>

      {/* Franja de Métricas Rápidas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-seryi-green/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Base Total Clientes</span>
            <div className="w-10 h-10 rounded-2xl bg-seryi-green/10 flex items-center justify-center text-seryi-green">
              <Users size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-seryi-heading">{clientsDatabase.length}</p>
          <p className="text-xs text-seryi-text mt-1">Con teléfono registrado</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-emerald-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Audiencia Objetivo</span>
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <Target size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-emerald-400">{filteredClients.length}</p>
          <p className="text-xs text-seryi-text mt-1">Filtrados en este segmento</p>
        </div>

        <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl relative overflow-hidden group hover:border-cyan-500/40 transition-all shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-seryi-text">Enviados Esta Sesión</span>
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 flex items-center justify-center text-cyan-400">
              <CheckCircle2 size={18} />
            </div>
          </div>
          <p className="text-3xl font-black text-cyan-400">{Object.keys(sentClients).length}</p>
          <p className="text-xs text-seryi-text mt-1">Disparados o entregados</p>
        </div>

        <div className="bg-gradient-to-br from-indigo-500/15 via-seryi-bg to-seryi-bg border border-indigo-500/30 p-6 rounded-3xl relative overflow-hidden group shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-400">Conexión Sistema Remoto</span>
            <ShieldCheck size={18} className="text-indigo-400" />
          </div>
          <p className="text-sm font-extrabold text-seryi-heading truncate">
            {activeBusiness?.webhook_n8n ? "Webhook Activo" : "Automatización Cloud"}
          </p>
          <p className="text-[11px] text-seryi-text mt-1">
            {activeBusiness?.webhook_n8n ? "Sincronización remota lista" : "Directo vía WhatsApp Web"}
          </p>
        </div>
      </div>

      <CampaignHistory campaigns={campaigns} messages={campaignMessages} missing={campaignsMissing} onStatus={setCampaignStatus} />

      {/* Política Anti-Bloqueo WhatsApp: los límites los aplica el servidor */}
      {showRateLimitPanel && (
        <div className="bg-gradient-to-r from-amber-950/30 via-seryi-bg to-seryi-bg border border-amber-500/30 p-6 rounded-3xl shadow-xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield size={18} className="text-amber-400" />
              <h3 className="text-base font-black text-seryi-heading">🛡️ Política Anti-Bloqueo WhatsApp</h3>
            </div>
            <button onClick={() => setShowRateLimitPanel(false)} className="text-seryi-text hover:text-seryi-heading" aria-label="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 text-xs text-amber-300 space-y-1">
            <p className="font-bold">⚠️ WhatsApp puede bloquear temporalmente tu número si envías demasiados mensajes en poco tiempo.</p>
            <p>Por eso los límites los aplica el servidor y no se pueden saltar:</p>
          </div>
          <ul className="text-xs text-seryi-text space-y-1 list-disc pl-5">
            <li>Un mensaje cada {CAMPAIGN_GAP_SECONDS} segundos o más, de lunes a sábado de 8 am a 8 pm.</li>
            <li>Máximo {CAMPAIGN_DAILY_LIMIT} mensajes por día; lo que sobre sigue al día siguiente.</li>
            <li>Máximo {CAMPAIGN_MAX_RECIPIENTS} contactos por campaña.</li>
            <li>No se escribe a chats silenciados ni a quien responda NO (cada mensaje lo invita a hacerlo).</li>
            <li>Si tu WhatsApp se desconecta, se reintenta solo (hasta 4 veces); si falla, ves el motivo en la campaña.</li>
          </ul>
        </div>
      )}

      {/* Constructor de Campaña: Segmentos, Editor de Mensaje y Vista Previa */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Columna Izquierda: Segmentación y Editor (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Selector de Segmento */}
          <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-seryi-heading flex items-center gap-2">
                <Filter size={18} className="text-seryi-green" />
                <span>1. Selecciona tu Segmento Objetivo</span>
              </h3>
              <span className="text-xs font-extrabold bg-seryi-green/15 text-seryi-green px-3 py-1 rounded-full">
                {filteredClients.length} Clientes
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { id: 'all', label: '👥 Todos los Clientes', desc: 'Toda tu base: WhatsApp, citas, pedidos y manuales' },
                { id: 'lead', label: '✨ Leads', desc: 'Te escribieron pero aún no agendan ni piden' },
                { id: 'cliente', label: '🛍️ Clientes', desc: 'Ya tienen citas o pedidos' },
                { id: 'vip', label: '⭐ VIP / Frecuentes', desc: 'Marcados VIP, 3+ visitas o $50k+ gastados' },
                { id: 'inactive', label: '😴 Inactivos (+30 días)', desc: 'Reactivación y retorno' },
                { id: 'recent', label: '⚡ Recientes (7 días)', desc: 'Feedback y re-agendamiento' }
              ].map(seg => (
                <button
                  key={seg.id}
                  onClick={() => setSelectedSegment(seg.id)}
                  className={`p-3.5 rounded-2xl text-left border transition-all duration-200 ${
                    selectedSegment === seg.id
                      ? 'bg-seryi-green/15 border-seryi-green text-seryi-heading shadow-md'
                      : 'bg-seryi-bg-dark/60 border-seryi-bg-light hover:border-seryi-green/30 text-seryi-text'
                  }`}
                >
                  <p className="text-xs font-extrabold text-seryi-heading">{seg.label}</p>
                  <p className="text-[11px] text-seryi-text mt-0.5">{seg.desc}</p>
                </button>
              ))}
            </div>

            {/* Explicación de origen de clientes */}
            <div className="bg-seryi-bg-dark/60 rounded-2xl p-3.5 border border-seryi-bg-light/40">
              <p className="text-[11px] text-seryi-text leading-relaxed">
                <strong className="text-seryi-heading">💡 ¿De dónde salen los clientes?</strong> De tu base de <strong className="text-seryi-green">Clientes</strong>: todo el que te escribe por WhatsApp, agenda una cita, hace un pedido o agregas a mano. Los segmentos se arman solos según su etapa.
                {contactsNotInterested > 0 && (
                  <span className="block mt-2 text-amber-300">
                    {contactsNotInterested} contacto{contactsNotInterested === 1 ? '' : 's'} marcado{contactsNotInterested === 1 ? '' : 's'} como Perdido no se incluye{contactsNotInterested === 1 ? '' : 'n'}: no les interesa o pidieron que no se les escriba.
                  </span>
                )}
                {contactsWithoutPhone > 0 && (
                  <span className="block mt-2 text-amber-300">
                    {contactsWithoutPhone} contacto{contactsWithoutPhone === 1 ? '' : 's'} no se incluye{contactsWithoutPhone === 1 ? '' : 'n'} porque WhatsApp no compartió su número. Vincúlalo en la pantalla Clientes para poder contactarlos.
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Selector de Plantilla Rápida */}
          <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl shadow-xl space-y-4">
            <h3 className="text-base font-black text-seryi-heading flex items-center gap-2">
              <Sparkles size={18} className="text-amber-400" />
              <span>2. Elige una Plantilla Base</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {defaultTemplates.map(tmp => (
                <button
                  key={tmp.id}
                  onClick={() => {
                    setActiveTemplateId(tmp.id);
                    setCustomText(tmp.text);
                  }}
                  className={`p-3.5 rounded-2xl text-left border transition-all duration-200 ${
                    activeTemplateId === tmp.id
                      ? 'bg-amber-500/15 border-amber-500 text-seryi-heading shadow-md'
                      : 'bg-seryi-bg-dark/60 border-seryi-bg-light hover:border-amber-500/30 text-seryi-text'
                  }`}
                >
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">
                    {tmp.category}
                  </span>
                  <p className="text-xs font-bold text-seryi-heading mt-2">{tmp.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Editor de Mensaje con Inserción de Variables Dinámicas */}
          <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl shadow-xl space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-seryi-heading flex items-center gap-2">
                <MessageSquare size={18} className="text-seryi-green" />
                <span>3. Constructor & Editor del Mensaje</span>
              </h3>
              <span className="text-[11px] text-slate-400 font-mono">
                {customText.length} caracteres
              </span>
            </div>

            {/* Chips de Variables Dinámicas */}
            <div className="space-y-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-seryi-text">
                Haz clic para insertar variables:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {CAMPAIGN_VARIABLES.map(v => (
                  <button
                    key={v.tag}
                    type="button"
                    onClick={() => handleInsertVariable(v.tag)}
                    className="px-2.5 py-1 bg-seryi-bg-dark hover:bg-seryi-green hover:text-seryi-bg-dark text-seryi-green border border-seryi-green/30 rounded-lg text-xs font-mono font-bold transition-all shadow-sm active:scale-95"
                    title={v.desc}
                  >
                    + {v.tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Textarea Editable */}
            <textarea
              ref={textareaRef}
              rows={5}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Escribe tu mensaje persuasivo con variables como {{nombre}}..."
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light focus:border-seryi-green rounded-2xl p-4 text-xs text-seryi-heading placeholder-seryi-text/50 outline-none leading-relaxed resize-none shadow-inner"
            />
          </div>
        </div>

        {/* Columna Derecha: Vista Previa en Vivo de WhatsApp (5 cols) */}
        <div className="lg:col-span-5 bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl shadow-xl flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-seryi-bg-light pb-3">
              <span className="text-xs font-extrabold uppercase tracking-wider text-seryi-heading flex items-center gap-2">
                <Smartphone size={16} className="text-emerald-400" />
                <span>Previsualización WhatsApp</span>
              </span>
              <span className="text-[10px] text-seryi-text bg-seryi-bg-dark px-2.5 py-1 rounded-full">
                Vista en Vivo
              </span>
            </div>

            {/* Celular Mockup Header */}
            <div className="bg-[#1f2c34] px-4 py-3 rounded-t-2xl flex items-center gap-3 border-b border-slate-700/60">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                {(activeBusiness?.nombre_negocio || 'S').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white truncate">{activeBusiness?.nombre_negocio || 'SeryiFlow'}</p>
                <p className="text-[10px] text-emerald-400 font-medium">en línea</p>
              </div>
            </div>

            {/* Burbuja estilo WhatsApp */}
            <div className="bg-[#0b141a] p-4 rounded-b-2xl border border-emerald-900/40 shadow-inner min-h-[200px] flex flex-col justify-end">
              <div className="bg-[#005c4b] text-white p-4 rounded-2xl rounded-tr-none text-xs leading-relaxed shadow-lg max-w-[95%] ml-auto relative">
                <p className="whitespace-pre-wrap font-medium">
                  {renderMessageForClient(filteredClients[0] || { name: "Sofía", phone: "573001234567" })}
                </p>
                <div className="flex items-center justify-end gap-1 mt-2 text-[10px] text-emerald-200/80">
                  <span>10:42 AM</span>
                  <CheckCircle2 size={12} className="text-sky-400" />
                </div>
              </div>
            </div>

            <div className="text-[11px] text-seryi-text space-y-1 pl-1">
              <p>💡 <strong className="text-seryi-heading">Reemplazo Dinámico:</strong> Vista previa con el primer cliente del segmento (<strong>{filteredClients[0]?.name || 'Sofía'}</strong>).</p>
              <p>🛡️ <strong className="text-seryi-heading">Protección Anti-Bloqueo:</strong> Envío desde el servidor, de a un mensaje cada {CAMPAIGN_GAP_SECONDS}s o más. Límite: {CAMPAIGN_DAILY_LIMIT} msgs/día.</p>
            </div>
          </div>

          <div className="space-y-2.5 pt-4 border-t border-seryi-bg-light">
            <button
              onClick={() => {
                const previewText = renderMessageForClient(filteredClients[0] || { name: "Sofía" });
                navigator.clipboard.writeText(previewText);
                toast.success("Mensaje copiado al portapapeles", { icon: '📋' });
              }}
              className="w-full py-3 bg-seryi-bg-dark hover:bg-seryi-bg-light text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 shadow-sm"
            >
              <Copy size={14} />
              <span>Copiar Texto Generado</span>
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Destinatarios y Envío Directo */}
      <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl shadow-xl overflow-hidden">
        <div className="p-6 border-b border-seryi-bg-light flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-black text-seryi-heading flex items-center gap-2">
              <span>4. Destinatarios ({filteredClients.length} Clientes)</span>
            </h3>
            <p className="text-xs text-seryi-text">
              Haz clic en "Enviar WhatsApp" para abrir el chat directo o usa el disparador masivo automático.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
              <input
                type="text"
                placeholder="Buscar por nombre o celular..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-9 pr-3 py-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl text-xs text-seryi-heading placeholder-seryi-text outline-none focus:border-seryi-green w-56"
              />
            </div>

            <span className="text-xs font-bold text-seryi-text bg-seryi-bg-dark px-3 py-2 rounded-xl border border-seryi-bg-light shrink-0">
              {Object.keys(sentClients).length} de {filteredClients.length} enviados
            </span>
          </div>
        </div>

        {filteredClients.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-seryi-bg-dark border border-seryi-bg-light flex items-center justify-center text-seryi-text mx-auto">
              <Users size={28} />
            </div>
            <h4 className="text-base font-bold text-seryi-heading">No hay clientes con este filtro</h4>
            <p className="text-xs text-seryi-text max-w-sm mx-auto">
              Prueba cambiando el segmento, borrando la búsqueda o importando nuevos contactos mediante el botón "Importar CSV".
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-seryi-bg-light text-[11px] font-extrabold uppercase text-seryi-text tracking-wider bg-seryi-bg-dark/40">
                  <th className="py-4 px-6">Cliente</th>
                  <th className="py-4 px-6 text-center">Celular</th>
                  <th className="py-4 px-6 text-right">Citas / Visitas</th>
                  <th className="py-4 px-6 text-right">Invertido</th>
                  <th className="py-4 px-6 text-center">Última Visita</th>
                  <th className="py-4 px-6 text-center">Estado</th>
                  <th className="py-4 px-6 text-center">Acción Directa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-seryi-bg-light/60 text-sm">
                {filteredClients.map(client => {
                  const isSent = sentClients[client.id];

                  return (
                    <tr key={client.id} className="hover:bg-seryi-bg-light/30 transition-colors group">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-seryi-green/10 border border-seryi-green/30 flex items-center justify-center font-black text-seryi-green text-xs">
                            {(client.name || 'C').charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-seryi-heading group-hover:text-seryi-green transition-colors">
                              {client.name}
                            </p>
                            <span className="text-[10px] text-seryi-text uppercase bg-seryi-bg-dark px-2 py-0.5 rounded">
                              {client.source}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="py-4 px-6 text-center text-xs font-mono text-seryi-text">
                        +{client.phone}
                      </td>

                      <td className="py-4 px-6 text-right font-bold text-seryi-heading">
                        {client.appointmentsCount}
                      </td>

                      <td className="py-4 px-6 text-right font-extrabold text-seryi-green">
                        ${Number(client.totalSpent || 0).toLocaleString('es-CO')}
                      </td>

                      <td className="py-4 px-6 text-center text-xs text-seryi-text">
                        {client.lastVisit}
                      </td>

                      <td className="py-4 px-6 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-extrabold uppercase ${
                            isSent
                              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                              : 'bg-slate-700/40 text-slate-400 border border-slate-600/50'
                          }`}
                        >
                          {isSent ? (
                            <>
                              <CheckCircle size={12} />
                              <span>ENVIADO</span>
                            </>
                          ) : (
                            <>
                              <Clock size={12} />
                              <span>PENDIENTE</span>
                            </>
                          )}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => handleSendSingle(client)}
                          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-md ${
                            isSent
                              ? 'bg-seryi-bg-dark text-seryi-text border border-seryi-bg-light hover:text-seryi-heading'
                              : 'bg-emerald-600 hover:bg-emerald-500 text-white hover:scale-105 shadow-emerald-600/20'
                          }`}
                        >
                          <Send size={13} />
                          <span>{isSent ? "Re-enviar" : "Enviar WhatsApp"}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal para Importar Contactos CSV / Lista */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-seryi-bg border border-seryi-bg-light text-seryi-heading w-full max-w-lg rounded-3xl p-6 md:p-8 relative shadow-2xl space-y-6">
            <button
              onClick={() => setShowImportModal(false)}
              className="absolute top-6 right-6 text-seryi-text hover:text-seryi-heading"
            >
              <X size={20} />
            </button>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-seryi-green text-xs font-bold uppercase tracking-wider">
                <Upload size={14} />
                <span>Importación Rápida</span>
              </div>
              <h3 className="text-xl font-black">Pegar o Cargar Contactos</h3>
              <p className="text-xs text-seryi-text">
                Pega tus clientes en formato <code>Nombre, Celular</code> (uno por línea).
              </p>
            </div>

            <textarea
              rows={6}
              value={rawImportText}
              onChange={(e) => setRawImportText(e.target.value)}
              placeholder={`Carlos Mendoza, 573001234567\nMaría Pérez, 573109876543\nAndrés Gómez, 573205556677`}
              className="w-full bg-seryi-bg-dark border border-seryi-bg-light focus:border-seryi-green rounded-2xl p-4 text-xs font-mono text-seryi-heading placeholder-seryi-text/40 outline-none resize-none"
            />

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-seryi-text hover:text-seryi-heading"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleProcessImport}
                className="px-5 py-2.5 bg-seryi-green hover:brightness-110 text-seryi-bg-dark rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md"
              >
                Procesar & Agregar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Agregar Contacto Individual */}
      {showAddContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-seryi-bg border border-seryi-bg-light text-seryi-heading w-full max-w-md rounded-3xl p-6 md:p-8 relative shadow-2xl space-y-5">
            <button
              onClick={() => setShowAddContactModal(false)}
              className="absolute top-6 right-6 text-seryi-text hover:text-seryi-heading"
            >
              <X size={20} />
            </button>

            <div className="space-y-1">
              <div className="flex items-center gap-2 text-seryi-green text-xs font-bold uppercase tracking-wider">
                <UserPlus size={14} />
                <span>Nuevo Contacto</span>
              </div>
              <h3 className="text-xl font-black">Agregar Cliente Manualmente</h3>
              <p className="text-xs text-seryi-text">
                Agrega un contacto a tu base de datos para futuras campañas. Se guarda de forma permanente.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-bold uppercase text-seryi-text mb-1">Nombre del Cliente *</label>
                <input
                  type="text"
                  placeholder="Ej: Carlos Mendoza"
                  value={newContactName}
                  onChange={(e) => setNewContactName(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light focus:border-seryi-green rounded-xl p-3 text-sm text-seryi-heading placeholder-seryi-text/40 outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase text-seryi-text mb-1">WhatsApp / Celular *</label>
                <input
                  type="text"
                  placeholder="Ej: 573001234567"
                  value={newContactPhone}
                  onChange={(e) => setNewContactPhone(e.target.value)}
                  className="w-full bg-seryi-bg-dark border border-seryi-bg-light focus:border-seryi-green rounded-xl p-3 text-sm text-seryi-heading placeholder-seryi-text/40 outline-none font-mono"
                />
                <span className="text-[10px] text-seryi-text/70 mt-1 block">Incluye código de país (ej: 57 para Colombia).</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowAddContactModal(false)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-seryi-text hover:text-seryi-heading"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={handleAddSingleContact}
                className="px-5 py-2.5 bg-seryi-green hover:brightness-110 text-seryi-bg-dark rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md"
              >
                Guardar Contacto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
